import {
  Badge,
  Button,
  Container,
  ExpandableSection,
  Header,
  Spinner,
} from "@amzn/awsui-components-console";
import React, { memo, useCallback, useEffect } from "react";

import { useCart } from "@/contexts/CartContext";
import { PreviewGroup, useClipPreview } from "@/contexts/ClipPreviewContext";
import { usePanelContent } from "@/contexts/PanelContentContext";
import useAssets from "@/hooks/cart/useAssets";
import { CLIP_PREVIEW_DRAWER_ID } from "@/utils/constants";

import { MetadataFilters } from "../chat/types";
import CartActionButton from "./CartActionButton";
import IconikClipTable from "./IconikClipTable";
import { EnumeratedIconikAsset, IconikAsset } from "./types";

export interface IconikAssetsTableProps {
  gameId?: string;
  playId?: string;
  assetId?: string;
  /** Response-level metadata filters applied to this play's asset fetch. */
  filters?: MetadataFilters;
  /**
   * Controls the accordion's expanded state. When provided, the section is a
   * controlled component and the parent owns its open/closed state (used by
   * the "Expand all / Collapse all" toggle in {@link IconikExpandables}).
   * When omitted, the section stays uncontrolled and defaults to collapsed —
   * matching the prior behaviour for any other caller.
   */
  expanded?: boolean;
  /**
   * Called when the user toggles this accordion. Only meaningful when
   * ``expanded`` is also supplied (controlled mode); the parent uses it to
   * update its per-section state.
   */
  onExpandChange?: (expanded: boolean) => void;
  /**
   * Reads every group in this answer, in screen order, to forward to the Clip
   * Preview so its arrows can continue past this group's last clip into the
   * next group's first. Only this accordion's own clips are ever handed over as
   * the preview's list; the sequence is how it finds the neighbours.
   *
   * A getter rather than the array, because the array gains an entry every time
   * a play resolves and a changing prop here would defeat this component's
   * memoization. Called only from the click handler and the registration
   * effect, never during render.
   *
   * Omitted leaves the preview confined to this group, as it was before.
   */
  getPreviewSequence?: () => PreviewGroup[];
  /** This accordion's key within the preview sequence. */
  previewGroupKey?: string;
}

const IconikAssetsTable: React.FC<IconikAssetsTableProps> = ({
  gameId = "",
  playId = "",
  assetId,
  filters,
  expanded,
  onExpandChange,
  getPreviewSequence,
  previewGroupKey,
}) => {
  const { isInCart, addToCart, removeFromCart } = useCart();
  const {
    openPreview,
    registerLatestPlay,
    activeClip,
    isOpen,
    gameId: previewGameId,
    playId: previewPlayId,
  } = useClipPreview();
  const { setActiveDrawerId } = usePanelContent();
  const { title, description, items, loading } = useAssets({
    gameId,
    playId,
    assetId,
    filters,
  });

  // The asset id currently shown in the Clip Preview, but only when the
  // preview is open on THIS play — so we highlight the right row.
  const activeAssetId =
    isOpen && previewGameId === gameId && previewPlayId === playId
      ? activeClip?.assetId
      : undefined;

  // Remember this accordion as the most recent play so the Clip Preview
  // toggle can fall back to it when no specific row has been clicked.
  useEffect(() => {
    if (items.length > 0) {
      registerLatestPlay({
        clips: items as EnumeratedIconikAsset[],
        index: 0,
        gameId,
        playId,
        // Carried here too, so opening the preview from the drawer's own toggle
        // gives the same cross-group arrows as clicking a row.
        sequence: getPreviewSequence?.(),
        groupKey: previewGroupKey,
      });
    }
  }, [
    items,
    gameId,
    playId,
    registerLatestPlay,
    getPreviewSequence,
    previewGroupKey,
  ]);

  const allInCart: boolean = (items as IconikAsset[]).every((value) =>
    isInCart(value)
  );

  // Stable handlers so the memoized IconikClipTable below only re-renders when
  // its data (or the active-row highlight) actually changes — not on every
  // Clip Preview / drawer state change that re-renders this owner component.
  const renderActions = useCallback(
    (item: EnumeratedIconikAsset) => (
      <CartActionButton
        item={item}
        inCart={isInCart(item)}
        onAdd={(asset) => addToCart([asset])}
        onRemove={(asset) => removeFromCart([asset.assetId])}
      />
    ),
    [isInCart, addToCart, removeFromCart]
  );

  const handleRowClick = useCallback(
    (_item: EnumeratedIconikAsset, index: number) => {
      openPreview({
        clips: items as EnumeratedIconikAsset[],
        index,
        gameId,
        playId,
        sequence: getPreviewSequence?.(),
        groupKey: previewGroupKey,
      });
      // Surface the Clip Preview drawer (this also closes Processing Steps
      // since only one drawer is open at a time).
      setActiveDrawerId(CLIP_PREVIEW_DRAWER_ID);
    },
    [
      openPreview,
      setActiveDrawerId,
      items,
      gameId,
      playId,
      getPreviewSequence,
      previewGroupKey,
    ]
  );

  return loading ? (
    <Container>
      <Header info={<Spinner />}>Loading...</Header>
    </Container>
  ) : items.length > 0 ? (
    <ExpandableSection
      headerText={title}
      headerDescription={description}
      headerCounter={`(${items.length})`}
      headerInfo={items.length <= 0 ? <Badge>No Media Found</Badge> : undefined}
      variant="container"
      {...(expanded !== undefined
        ? {
            expanded,
            onChange: ({ detail }) => onExpandChange?.(detail.expanded),
          }
        : {})}
      headerActions={
        <Button
          disabled={allInCart}
          onClick={() => addToCart(items as IconikAsset[])}
          variant="inline-link"
          iconAlign="right"
        >
          {allInCart ? "All in Cart" : "Add All to Cart"}
        </Button>
      }
    >
      <IconikClipTable
        items={items as EnumeratedIconikAsset[]}
        loading={loading}
        activeAssetId={activeAssetId}
        actionsHeader="Add to Cart"
        renderActions={renderActions}
        onRowClick={handleRowClick}
      />
    </ExpandableSection>
  ) : (
    <Container>
      <Header info={<Badge>No Media Found</Badge>} description={description}>
        {title}
      </Header>
    </Container>
  );
};

export default memo(IconikAssetsTable);
